import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './SkillSelector.css';

export interface SkillSelectorProps {
  value: string[];
  onChange: (skills: string[]) => void;
  error?: string;
  showNextButton?: boolean;
  onNextQuestion?: () => void;
}

const POPULAR_SKILLS = [
  'JavaScript/TypeScript',
  'Python',
  'React/Next.js',
  'Node.js',
  'Full-stack Development',
  'UI/UX Design',
  'Digital Marketing',
  'Content Creation',
  'Data Analytics',
  'Project Management',
  'AWS/Amazon Web Services',
  'Mobile Development (iOS/Android)'
];

// Category keys for translation
const CATEGORY_KEYS = {
  'Technical Development': 'categoryTechnicalDevelopment',
  'Programming Languages': 'categoryProgrammingLanguages',
  'Frameworks & Libraries': 'categoryFrameworksLibraries',
  'Data & AI/ML': 'categoryDataAIML',
  'Design & UX': 'categoryDesignUX',
  'Cloud & Infrastructure': 'categoryCloudInfrastructure',
  'Business & Marketing': 'categoryBusinessMarketing',
  'Sales & Customer Success': 'categorySalesCustomer',
  'Project & Product Management': 'categoryProjectProduct',
  'Finance & Operations': 'categoryFinanceOperations'
};

// Skill translation keys (only for skills that should be translated)
const SKILL_TRANSLATION_KEYS: Record<string, string> = {
  // Technical Development
  'Front-end Development': 'skillFrontendDev',
  'Back-end Development': 'skillBackendDev',
  'Full-stack Development': 'skillFullstackDev',
  'Mobile Development (iOS/Android)': 'skillMobileDev',
  'DevOps/Infrastructure': 'skillDevOps',
  'Database Management': 'skillDatabaseMgmt',
  'API Development': 'skillApiDev',
  'Microservices Architecture': 'skillMicroservices',
  'System Architecture': 'skillSystemArch',
  'Web Development': 'skillWebDev',
  // Data & AI/ML
  'Data Science': 'skillDataScience',
  'Machine Learning': 'skillMachineLearning',
  'Deep Learning': 'skillDeepLearning',
  'Data Analytics': 'skillDataAnalytics',
  'Business Intelligence': 'skillBusinessIntel',
  'Data Visualization': 'skillDataViz',
  'Statistical Analysis': 'skillStatAnalysis',
  'Big Data Processing': 'skillBigData',
  'Natural Language Processing': 'skillNLP',
  'Computer Vision': 'skillComputerVision',
  // Design & UX
  'UI/UX Design': 'skillUIUXDesign',
  'Graphic Design': 'skillGraphicDesign',
  'Product Design': 'skillProductDesign',
  'User Research': 'skillUserResearch',
  'Prototyping': 'skillPrototyping',
  'Design Systems': 'skillDesignSystems',
  'Web Design': 'skillWebDesign',
  'Branding': 'skillBranding',
  // Cloud & Infrastructure
  'CI/CD Pipelines': 'skillCICD',
  'Infrastructure as Code': 'skillIaC',
  'Server Management': 'skillServerMgmt',
  'Monitoring & Logging': 'skillMonitoring',
  'Security & Compliance': 'skillSecurity',
  'Networking': 'skillNetworking',
  // Business & Marketing
  'Digital Marketing': 'skillDigitalMarketing',
  'Content Creation': 'skillContentCreation',
  'Social Media Marketing': 'skillSocialMedia',
  'Email Marketing': 'skillEmailMarketing',
  'Content Marketing': 'skillContentMarketing',
  'Affiliate Marketing': 'skillAffiliateMarketing',
  'Influencer Marketing': 'skillInfluencerMarketing',
  'Email Automation': 'skillEmailAutomation',
  'Growth Hacking': 'skillGrowthHacking',
  // Sales & Customer Success
  'Sales': 'skillSales',
  'Customer Support': 'skillCustomerSupport',
  'Customer Success': 'skillCustomerSuccess',
  'Account Management': 'skillAccountMgmt',
  'Lead Generation': 'skillLeadGen',
  'Sales Funnel Optimization': 'skillSalesFunnel',
  'CRM Management': 'skillCRMMgmt',
  'Business Development': 'skillBizDev',
  'Relationship Building': 'skillRelationships',
  'Negotiation': 'skillNegotiation',
  // Project & Product Management
  'Project Management': 'skillProjectMgmt',
  'Product Management': 'skillProductMgmt',
  'Business Analysis': 'skillBizAnalysis',
  'Requirements Gathering': 'skillRequirements',
  'Roadmap Planning': 'skillRoadmap',
  'Stakeholder Management': 'skillStakeholders',
  'Risk Management': 'skillRiskMgmt',
  'Quality Assurance': 'skillQA',
  'Process Optimization': 'skillProcessOpt',
  // Finance & Operations
  'Financial Analysis': 'skillFinAnalysis',
  'Accounting': 'skillAccounting',
  'Operations Management': 'skillOpsMgmt',
  'Supply Chain': 'skillSupplyChain',
  'Human Resources': 'skillHR',
  'Legal & Compliance': 'skillLegal',
  'Strategic Planning': 'skillStrategy',
  'Performance Analytics': 'skillPerfAnalytics',
  'Cost Optimization': 'skillCostOpt'
};

const SKILL_CATEGORIES = {
  'Technical Development': [
    'Front-end Development',
    'Back-end Development',
    'Full-stack Development',
    'Mobile Development (iOS/Android)',
    'DevOps/Infrastructure',
    'Database Management',
    'API Development',
    'Microservices Architecture',
    'System Architecture',
    'Web Development'
  ],
  'Programming Languages': [
    'JavaScript/TypeScript',
    'Python',
    'Java',
    'C#/.NET',
    'PHP',
    'Go',
    'Rust',
    'Swift',
    'Kotlin',
    'Ruby',
    'C/C++',
    'Scala',
    'R'
  ],
  'Frameworks & Libraries': [
    'React/Next.js',
    'Vue.js',
    'Angular',
    'Node.js',
    'Django/Flask',
    'Laravel',
    'Spring Boot',
    'Express.js',
    'WordPress',
    'Shopify',
    'Flutter/Dart',
    'React Native'
  ],
  'Data & AI/ML': [
    'Data Science',
    'Machine Learning',
    'Deep Learning',
    'Data Analytics',
    'Business Intelligence',
    'Data Visualization',
    'Statistical Analysis',
    'Big Data Processing',
    'Natural Language Processing',
    'Computer Vision'
  ],
  'Design & UX': [
    'UI/UX Design',
    'Graphic Design',
    'Product Design',
    'User Research',
    'Prototyping',
    'Design Systems',
    'Adobe Creative Suite',
    'Figma/Sketch',
    'Web Design',
    'Branding'
  ],
  'Cloud & Infrastructure': [
    'AWS/Amazon Web Services',
    'Google Cloud Platform',
    'Microsoft Azure',
    'Docker/Kubernetes',
    'CI/CD Pipelines',
    'Infrastructure as Code',
    'Server Management',
    'Monitoring & Logging',
    'Security & Compliance',
    'Networking'
  ],
  'Business & Marketing': [
    'Digital Marketing',
    'Content Creation',
    'SEO/SEM',
    'Social Media Marketing',
    'Email Marketing',
    'Content Marketing',
    'Affiliate Marketing',
    'Influencer Marketing',
    'Email Automation',
    'Growth Hacking'
  ],
  'Sales & Customer Success': [
    'Sales',
    'Customer Support',
    'Customer Success',
    'Account Management',
    'Lead Generation',
    'Sales Funnel Optimization',
    'CRM Management',
    'Business Development',
    'Relationship Building',
    'Negotiation'
  ],
  'Project & Product Management': [
    'Project Management',
    'Product Management',
    'Agile/Scrum',
    'Business Analysis',
    'Requirements Gathering',
    'Roadmap Planning',
    'Stakeholder Management',
    'Risk Management',
    'Quality Assurance',
    'Process Optimization'
  ],
  'Finance & Operations': [
    'Financial Analysis',
    'Accounting',
    'Operations Management',
    'Supply Chain',
    'Human Resources',
    'Legal & Compliance',
    'Strategic Planning',
    'Business Intelligence',
    'Performance Analytics',
    'Cost Optimization'
  ]
};

// Flatten all skills for easy searching
const AVAILABLE_SKILLS = Object.values(SKILL_CATEGORIES).flat();

export const SkillSelector: React.FC<SkillSelectorProps> = ({
  value,
  onChange,
  error,
  showNextButton = false,
  onNextQuestion
}) => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('Popular');

  const filteredSkills = searchTerm 
    ? AVAILABLE_SKILLS.filter(skill =>
        skill.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : activeCategory === 'Popular'
    ? POPULAR_SKILLS
    : activeCategory === 'All'
    ? AVAILABLE_SKILLS
    : SKILL_CATEGORIES[activeCategory as keyof typeof SKILL_CATEGORIES] || POPULAR_SKILLS;

  const handleSkillToggle = (skill: string) => {
    if (value.includes(skill)) {
      onChange(value.filter(s => s !== skill));
    } else {
      onChange([...value, skill]);
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    onChange(value.filter(skill => skill !== skillToRemove));
  };

  const handleSelectAll = () => {
    const categorySkills = activeCategory === 'Popular' 
      ? POPULAR_SKILLS 
      : activeCategory === 'All'
      ? AVAILABLE_SKILLS
      : SKILL_CATEGORIES[activeCategory as keyof typeof SKILL_CATEGORIES] || [];
    
    const newSkills = [...new Set([...value, ...categorySkills])];
    onChange(newSkills);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const getSkillCategory = (skill: string) => {
    if (POPULAR_SKILLS.includes(skill)) return t('aiIdeaGenerator.popular');
    for (const [category, skills] of Object.entries(SKILL_CATEGORIES)) {
      if (skills.includes(skill)) {
        return t(`aiIdeaGenerator.${CATEGORY_KEYS[category as keyof typeof CATEGORY_KEYS]}`);
      }
    }
    return 'Other';
  };

  const getTranslatedSkillName = (skill: string) => {
    const translationKey = SKILL_TRANSLATION_KEYS[skill];
    if (translationKey) {
      return t(`aiIdeaGenerator.${translationKey}`);
    }
    return skill; // Return original if no translation
  };

  return (
    <div className="skill-selector">
      {error && <div className="error-text" style={{marginBottom: '1rem'}}>{error}</div>}
      
      <div className="selector-layout">
        {/* Left Panel - Categories */}
        <div className="categories-panel">
          <h4>{t('aiIdeaGenerator.categories')}</h4>
          <div className="category-list">
            <div 
              className={`category-item ${activeCategory === 'Popular' ? 'active' : ''}`}
              onClick={() => setActiveCategory('Popular')}
            >
              <span>⭐ {t('aiIdeaGenerator.popular')}</span>
              <span className="category-count">{POPULAR_SKILLS.length}</span>
            </div>
            <div 
              className={`category-item ${activeCategory === 'All' ? 'active' : ''}`}
              onClick={() => setActiveCategory('All')}
            >
              <span>📋 {t('aiIdeaGenerator.allSkills')}</span>
              <span className="category-count">{AVAILABLE_SKILLS.length}</span>
            </div>
            {Object.entries(SKILL_CATEGORIES).map(([category, skills]) => (
              <div 
                key={category}
                className={`category-item ${activeCategory === category ? 'active' : ''}`}
                onClick={() => setActiveCategory(category)}
              >
                <span>{t(`aiIdeaGenerator.${CATEGORY_KEYS[category as keyof typeof CATEGORY_KEYS]}`)}</span>
                <span className="category-count">{skills.length}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Middle Panel - Skills List */}
        <div className="items-panel">
          <div className="items-header">
            <div className="search-and-actions">
              <input
                type="text"
                placeholder={t('aiIdeaGenerator.searchSkillsGlobally')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="global-search"
              />
              <button 
                onClick={handleSelectAll}
                className="select-all-btn"
                type="button"
              >
                {t('aiIdeaGenerator.selectAllIn')} {activeCategory}
              </button>
            </div>
          </div>
          
          <div className="items-summary">
            {t('aiIdeaGenerator.showing')} {filteredSkills.length} {t('aiIdeaGenerator.skills')} • {value.length} {t('aiIdeaGenerator.selected')}
          </div>
          
          <div className="items-grid">
            {filteredSkills.map(skill => (
              <div
                key={skill}
                className={`item-option ${value.includes(skill) ? 'selected' : ''}`}
                onClick={() => handleSkillToggle(skill)}
              >
                <input
                  type="checkbox"
                  checked={value.includes(skill)}
                  onChange={() => handleSkillToggle(skill)}
                  className="item-checkbox"
                />
                <span className="item-name">{getTranslatedSkillName(skill)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel - Selected Skills */}
        <div className="selected-panel">
          <div className="selected-header">
            <h4 className="selected-title">{t('aiIdeaGenerator.selectedSkills')}</h4>
            <span className="selected-count">{value.length}</span>
            {value.length > 0 && (
              <button onClick={handleClearAll} className="clear-all-btn" type="button">
                {t('aiIdeaGenerator.clearAll')}
              </button>
            )}
          </div>
          
          <div className="selected-items-list">
            {value.length === 0 ? (
              <div className="empty-selection">
                <div className="empty-selection-icon">🎯</div>
                <div>{t('aiIdeaGenerator.selectFromList')}</div>
              </div>
            ) : (
              value.map(skill => (
                <div key={skill} className="selected-item">
                  <div>
                    <div className="selected-item-name">{getTranslatedSkillName(skill)}</div>
                    <div className="selected-item-category">{getSkillCategory(skill)}</div>
                  </div>
                  <button
                    onClick={() => handleRemoveSkill(skill)}
                    className="remove-item-btn"
                    type="button"
                    aria-label={`${t('aiIdeaGenerator.remove')} ${getTranslatedSkillName(skill)}`}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
          
          {/* Next Question Button - Fixed at bottom */}
          {showNextButton && value.length > 0 && (
            <div className="next-question-container">
              <button
                onClick={onNextQuestion}
                className="next-question-btn-inline"
                type="button"
              >
                <span className="btn-icon">→</span>
                {t('aiIdeaGenerator.nextQuestion')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};